import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Pencil, Archive, ArchiveRestore, AlarmClock, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { php, phone, date, dateTime, label } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SelectBox, StatusBadge, DemoTag, Empty } from "@/components/common/ui";
import LeadFormDialog from "@/components/leads/LeadFormDialog";
import StageMoveDialog, { moveLead } from "@/components/leads/StageMoveDialog";
import TaskDialog from "@/components/tasks/TaskDialog";
import QuoteDialog from "@/components/quotes/QuoteDialog";

function Field({ k, v }) {
  return (
    <div>
      <div className="eyebrow">{k}</div>
      <div className="text-sm mt-0.5 break-words">{v || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

function ActivityTab({ lead, reload }) {
  const { can } = useAuth();
  const [type, setType] = useState("note");
  const [text, setText] = useState("");
  const add = async () => {
    try {
      await api.post(`/leads/${lead.id}/activities`, { type, text });
      setText("");
      toast.success("Activity recorded");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4">
      {can("activities.create") && (
        <div className="border rounded p-3 bg-slate-50 space-y-2">
          <div className="flex gap-2">
            <SelectBox value={type} onChange={(v) => setType(v || "note")} testid="activity-type-select" className="w-36"
              options={["note", "call", "visit", "email", "meeting", "order"].map((t) => ({ value: t, label: label(t) }))} />
          </div>
          <Textarea placeholder="What happened?" value={text} onChange={(e) => setText(e.target.value)} data-testid="activity-text-input" />
          <Button size="sm" onClick={add} disabled={!text.trim()} className="bg-[#0B192C]" data-testid="activity-add-button">Record activity</Button>
        </div>
      )}
      <ol className="relative border-l border-slate-200 ml-2 space-y-4" data-testid="activity-timeline">
        {lead.activities.map((a) => (
          <li key={a.id} className="ml-4">
            <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ${a.type === "stage_change" ? "bg-orange-500" : "bg-slate-400"}`} />
            <div className="text-xs text-slate-400">{dateTime(a.created_at)} · {label(a.type)} · {a.user_name}</div>
            <div className="text-sm mt-0.5 whitespace-pre-wrap">{a.text}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TasksTab({ lead, reload }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const done = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { done: true });
      toast.success("Follow-up completed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const now = new Date().toISOString();
  return (
    <div className="space-y-3">
      {can("tasks.manage") && <Button size="sm" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="lead-add-task-button">Schedule follow-up</Button>}
      {lead.tasks.length === 0 && <Empty>No follow-ups yet.</Empty>}
      {lead.tasks.map((t) => (
        <div key={t.id} className={`flex items-center justify-between gap-3 border rounded p-3 ${!t.done && t.due_at < now ? "border-red-300 bg-red-50" : "bg-white"}`} data-testid={`lead-task-${t.id}`}>
          <div>
            <div className={`text-sm font-medium ${t.done ? "line-through text-slate-400" : ""}`}>{t.title}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1"><AlarmClock className="h-3 w-3" />{dateTime(t.due_at)}{!t.done && t.due_at < now && <b className="text-red-600 ml-1">OVERDUE</b>}</div>
          </div>
          {!t.done && can("tasks.manage") && <Button size="sm" variant="outline" onClick={() => done(t)} data-testid={`complete-task-${t.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Done</Button>}
        </div>
      ))}
      <TaskDialog open={open} onOpenChange={setOpen} leadId={lead.id} onSaved={reload} />
    </div>
  );
}

function QuotesTab({ lead, reload }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      {can("quotations.create") && <Button size="sm" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="lead-add-quote-button">New quotation</Button>}
      {lead.quotations.length === 0 && <Empty>No quotations yet.</Empty>}
      {lead.quotations.map((q) => (
        <div key={q.id} className="flex items-center justify-between border rounded p-3 bg-white">
          <div><div className="text-sm font-medium num">{q.ref_no}</div><div className="text-xs text-slate-500">{date(q.created_at)} · {q.attachments.length} attachment(s)</div></div>
          <div className="text-right"><div className="num text-sm font-semibold">{php(q.amount)}</div><StatusBadge status={q.status} /></div>
        </div>
      ))}
      <QuoteDialog open={open} onOpenChange={setOpen} leadId={lead.id} onSaved={reload} />
    </div>
  );
}

export default function LeadDrawer({ leadId, onClose, onChanged }) {
  const { meta, can, user } = useAuth();
  const [lead, setLead] = useState(null);
  const [edit, setEdit] = useState(false);
  const [move, setMove] = useState(null);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
    } catch (e) {
      toast.error(errMsg(e));
      onClose();
    }
  }, [leadId, onClose]);
  useEffect(() => { setLead(null); load(); }, [load]);

  const changed = () => { load(); onChanged?.(); };
  const market = lead && meta.markets.find((m) => m.key === lead.market);
  const canEdit = lead && (can("leads.edit") || (can("leads.edit_own") && lead.assigned_to === user.id));

  const pickStage = async (sid) => {
    const stage = market.stages.find((s) => s.id === sid);
    if (!stage || stage.id === lead.stage_id) return;
    if (stage.type !== "open") return setMove({ lead, stage });
    try {
      await moveLead(lead, stage);
      toast.success(`Moved to ${stage.name}`);
      changed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const archive = async () => {
    try {
      await api.post(`/leads/${lead.id}/archive`, null, { params: { archived: !lead.archived } });
      toast.success(lead.archived ? "Lead restored" : "Lead archived");
      changed();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Sheet open={!!leadId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="lead-drawer">
        {!lead ? <div className="p-8 text-slate-400">Loading…</div> : (
          <>
            <SheetHeader className="bg-[#0B192C] text-white p-6 text-left space-y-1">
              <div className="text-[11px] uppercase tracking-[0.16em] text-orange-400">{market?.name}</div>
              <SheetTitle className="text-white text-2xl" data-testid="lead-drawer-name">{lead.name}<DemoTag show={lead.is_demo} /></SheetTitle>
              <div className="text-slate-400 text-sm">{lead.company}</div>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <StatusBadge status={lead.status} testid="lead-drawer-status" />
                {lead.archived && <span className="text-xs bg-slate-700 px-2 py-0.5 rounded">Archived</span>}
                {lead.overdue && <span className="text-xs bg-red-600 px-2 py-0.5 rounded">Overdue follow-up</span>}
              </div>
            </SheetHeader>
            <div className="p-6 space-y-5">
              <div className="flex flex-wrap gap-2 items-center">
                {can("leads.move_stage") && canEdit && !lead.archived ? (
                  <SelectBox value={lead.stage_id} onChange={pickStage} testid="lead-stage-select" className="w-60" options={market.stages.map((s) => ({ value: s.id, label: s.name }))} />
                ) : <span className="text-sm">Stage: <b>{lead.stage_name}</b></span>}
                {canEdit && <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="lead-edit-button"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
                {can("leads.archive") && <Button variant="outline" size="sm" onClick={archive} data-testid="lead-archive-button">{lead.archived ? <ArchiveRestore className="h-4 w-4 mr-1" /> : <Archive className="h-4 w-4 mr-1" />}{lead.archived ? "Restore" : "Archive"}</Button>}
              </div>
              <Tabs defaultValue="overview">
                <TabsList className="w-full grid grid-cols-4">
                  <TabsTrigger value="overview" data-testid="lead-tab-overview">Overview</TabsTrigger>
                  <TabsTrigger value="activity" data-testid="lead-tab-activity">Activity</TabsTrigger>
                  <TabsTrigger value="tasks" data-testid="lead-tab-tasks">Follow-ups</TabsTrigger>
                  <TabsTrigger value="quotes" data-testid="lead-tab-quotes">Quotes</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field k="Mobile" v={lead.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{phone(lead.phone)}</span>} />
                    <Field k="Email" v={lead.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{lead.email}</span>} />
                    <Field k="Location" v={lead.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{lead.location}</span>} />
                    <Field k="Source" v={lead.source} />
                    <Field k="Assigned to" v={lead.assigned_name || "Unassigned"} />
                    <Field k="Estimated value" v={<span className="num">{php(lead.value)}</span>} />
                    {lead.status === "won" && <Field k="Won value" v={<span className="num text-emerald-700 font-semibold">{php(lead.won_value)}</span>} />}
                    {lead.status === "lost" && <Field k="Lost reason" v={lead.lost_reason} />}
                    <Field k="Next follow-up" v={lead.next_follow_up && dateTime(lead.next_follow_up)} />
                    <Field k="Created" v={date(lead.created_at)} />
                    {market.fields.map((f) => <Field key={f.key} k={f.label} v={f.type === "number" && lead.custom?.[f.key] != null && /PHP/.test(f.label) ? php(lead.custom[f.key]) : lead.custom?.[f.key]} />)}
                  </div>
                </TabsContent>
                <TabsContent value="activity" className="pt-4"><ActivityTab lead={lead} reload={changed} /></TabsContent>
                <TabsContent value="tasks" className="pt-4"><TasksTab lead={lead} reload={changed} /></TabsContent>
                <TabsContent value="quotes" className="pt-4"><QuotesTab lead={lead} reload={changed} /></TabsContent>
              </Tabs>
            </div>
            <LeadFormDialog open={edit} onOpenChange={setEdit} lead={lead} onSaved={changed} />
            <StageMoveDialog move={move} onClose={() => setMove(null)} onMoved={changed} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
